'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import { Calendar, User, Clock, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import Newsletter from '@/components/Newsletter';
import JourneyCTA from '@/components/JourneyCTA';
import { BlogPostDetail } from '@/services/blogService';
import BlogCard from '@/components/BlogCard';
import ContentWithShortcodes from '@/components/ContentWithShortcodes';

interface BlogPostClientProps {
  post: BlogPostDetail;
}

export default function BlogPostClient({ post }: BlogPostClientProps) {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0); // First FAQ open by default

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const calculateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(' ').length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const shareOnSocial = (platform: string) => {
    const url = window.location.href;
    const title = post.title;
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'pinterest':
        shareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(title)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <Header variant="fixed" />

      {/* Article Header */}
      <section className="pt-32 pb-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex flex-wrap items-center space-x-2 text-sm text-gray-600 leading-loose">
                <li><Link href="/blog" className="hover:text-blue-600" prefetch={false}>Blog</Link></li>
                <li>/</li>
                <li><Link href={`/blog/category/${post.category.slug}`} className="hover:text-blue-600" prefetch={false}>{post.category.name}</Link></li>
                <li>/</li>
                <li className="text-gray-900">{post.title}</li>
              </ol>
            </nav>

            {/* Article Meta */}
            <div className="mb-6">
              <span className="px-3 py-1 text-white text-sm font-medium rounded-full" style={{ backgroundColor: '#6B39FF' }}>
                {post.category.name}
              </span>
            </div>

            {/* Article Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ lineHeight: 1.3 }}>
              {post.title}
            </h1>

            {/* Article Excerpt */}
            <p className="text-xl max-[599px]:text-[1.1rem] text-gray-600 mb-8 leading-relaxed">
              {post.excerpt}
            </p>

            {/* Article Info */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                {post.authorName}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(post.publishedAt)}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {calculateReadTime(post.content)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="max-w-4xl mx-auto">
              {/* Main Content */}
              <div className="w-full">
                {/* Highlights */}
                {post.highlights && (
                  <div className="mb-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
                    <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      Key Highlights
                    </h3>
                    <div 
                      className="content-prose-sm text-blue-800 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-2 [&_ul]:space-y-2 [&_ul]:list-outside [&_ul]:ml-4"
                      style={{
                        '--tw-prose-bullets': '#1e40af'
                      } as React.CSSProperties}
                      dangerouslySetInnerHTML={{ __html: post.highlights }}
                    />
                  </div>
                )}

                {/* Featured Image - container has no fixed height; image height auto, position relative */}
                {post.featuredImage && (
                  <div className="mb-8 rounded-xl overflow-hidden">
                    <Image
                      src={post.featuredImage}
                      alt={post.title}
                      width={1200}
                      height={630}
                      className="relative w-full h-auto rounded-xl"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
                      priority
                      unoptimized
                    />
                  </div>
                )}

                {/* Article Body */}
                <article>
                  <ContentWithShortcodes html={post.content} />
                </article>

                {/* FAQ Section */}
                {post.blog_faqs && post.blog_faqs.length > 0 && (
                  <div className="mt-12">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
                    <div className="space-y-4">
                      {post.blog_faqs.map((faq, index) => (
                        <div key={faq.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => toggleFAQ(index)}
                            className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                          >
                            <h4 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h4>
                            {openFAQ === index ? (
                              <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                            )}
                          </button>
                          <div 
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className="px-6 py-4 bg-white border-t border-gray-200">
                              <div 
                                className="content-prose-sm"
                                dangerouslySetInnerHTML={{ __html: faq.answer }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share - end of article */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Share this article</h3>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => shareOnSocial('facebook')}
                        className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
                        aria-label="Share on Facebook"
                        title="Share on Facebook"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => shareOnSocial('twitter')}
                        className="w-9 h-9 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-gray-900 transition-colors"
                        aria-label="Share on X (Twitter)"
                        title="Share on X (Twitter)"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => shareOnSocial('pinterest')}
                        className="w-9 h-9 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors"
                        aria-label="Pin on Pinterest"
                        title="Pin on Pinterest"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => shareOnSocial('whatsapp')}
                        className="w-9 h-9 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 transition-colors"
                        aria-label="Share on WhatsApp"
                        title="Share on WhatsApp"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors duration-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Author Bio */}
                <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">About the Author</h3>
                  <p className="text-gray-600">
                    {post.authorName} is an expert in {post.category.name.toLowerCase()} with years of experience helping individuals 
                    navigate their matrimonial journey. Their insights and guidance have helped countless people 
                    find meaningful relationships.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey CTA Section */}
      <JourneyCTA 
        title="Ready to Start Your Journey?"
        description="Discover meaningful connections through our trusted Marathi matrimony platform built with the Marathi community's values at heart"
        primaryButtonText="Register Now in 2 minutes"
        primaryButtonLink="/register"
      />

      {/* Related Articles */}
      {post.relatedBlogs && post.relatedBlogs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {post.relatedBlogs.map((relatedPost) => (
                  <BlogCard key={relatedPost.id} post={relatedPost} formatDate={formatDate} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Signup */}
      <Newsletter />
    </div>
  );
}
